import { useState, useEffect } from 'react'
import Button from '../../../components/common/Button/Button'
import { uploadSalarySlip, formatFileSize, validatePDF } from '../../../services/cloudinaryService'
import {
  getAllEmployees,
  createSalarySlip,
  getAllMonths,
  getYearOptions,
  checkDuplicate
} from '../../../services/salarySlipsService'

/**
 * UploadForm Component
 * Admin form for uploading salary slips
 *
 * @param {function} onUploadSuccess - Callback when upload succeeds
 * @param {string} currentUserUid - Current admin user UID
 * @param {string} currentUserName - Current admin user name
 */
const UploadForm = ({ onUploadSuccess, currentUserUid, currentUserName }) => {
  // State
  const [employees, setEmployees] = useState([])
  const [selectedEmployee, setSelectedEmployee] = useState('')
  const [month, setMonth] = useState('')
  const [year, setYear] = useState(new Date().getFullYear())
  const [file, setFile] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  // Load employees on mount
  useEffect(() => {
    loadEmployees()
  }, [])

  const loadEmployees = async () => {
    const result = await getAllEmployees()
    if (result.success) {
      setEmployees(result.data)
    } else {
      setError('Failed to load employees')
    }
  }

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0]
    setError('')
    setSuccess('')

    if (selectedFile) {
      const validation = validatePDF(selectedFile)
      if (!validation.valid) {
        setError(validation.error)
        setFile(null)
        e.target.value = '' // Reset file input
        return
      }
      setFile(selectedFile)
    }
  }

  const resetForm = () => {
    setSelectedEmployee('')
    setMonth('')
    setYear(new Date().getFullYear())
    setFile(null)
    setError('')
    setSuccess('')

    // Reset file input
    const fileInput = document.querySelector('input[type="file"]')
    if (fileInput) fileInput.value = ''
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setUploading(true)
    setError('')
    setSuccess('')

    try {
      // Validate all fields
      if (!selectedEmployee || !month || !year || !file) {
        throw new Error('Please fill in all fields')
      }

      // Get selected employee data
      const employee = employees.find(emp => emp.uid === selectedEmployee)
      if (!employee) {
        throw new Error('Selected employee not found')
      }

      // Check for duplicate
      const duplicate = await checkDuplicate(employee.employeeId, month, year)
      if (duplicate.exists) {
        throw new Error(`Salary slip for ${month} ${year} already exists for ${employee.fullName}`)
      }

      // Upload to Cloudinary
      const uploadResult = await uploadSalarySlip(file, employee.employeeId, month, year)

      if (!uploadResult.success) {
        throw new Error(uploadResult.error || 'Failed to upload file')
      }

      // Save metadata to Firestore
      const slipData = {
        employeeId: employee.employeeId,
        employeeName: employee.fullName,
        employeeEmail: employee.email,
        month: month,
        year: year,
        cloudinaryUrl: uploadResult.data.cloudinaryUrl,
        cloudinaryPublicId: uploadResult.data.publicId,
        uploadedBy: currentUserUid,
        uploadedByName: currentUserName,
        fileSize: uploadResult.data.fileSize,
        fileName: file.name
      }

      const createResult = await createSalarySlip(slipData)

      if (!createResult.success) {
        throw new Error(createResult.error || 'Failed to save salary slip metadata')
      }

      // Success!
      setSuccess(`Salary slip uploaded successfully for ${employee.fullName} - ${month} ${year}`)

      // Call success callback with uploadedAt timestamp
      if (onUploadSuccess) {
        onUploadSuccess({
          id: createResult.docId,
          ...slipData,
          uploadedAt: createResult.uploadedAt  // Include timestamp from server
        })
      }

      // Reset form after 2 seconds
      setTimeout(() => {
        resetForm()
      }, 2000)

    } catch (error) {
      console.error('Upload error:', error)
      setError(error.message || 'An error occurred during upload')
    } finally {
      setUploading(false)
    }
  }

  return (
    <div>
      <div style={{
        marginBottom: '1.5rem',
        paddingBottom: '1rem',
        borderBottom: '2px solid var(--psyber-primary)'
      }}>
        <h3 style={{
          margin: 0,
          fontSize: 'var(--psyber-font-size-xl)',
          color: 'var(--psyber-text)',
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem'
        }}>
          <i className="fas fa-upload"></i>
          Upload Salary Slip
        </h3>
        <p style={{
          margin: '0.5rem 0 0 0',
          fontSize: 'var(--psyber-font-size-sm)',
          color: 'var(--psyber-text-secondary)'
        }}>
          Upload PDF salary slips for employees (Max 10MB)
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem',
          marginBottom: '1rem'
        }}>
          {/* Employee Dropdown */}
          <div>
            <label
              htmlFor="employee"
              style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontSize: 'var(--psyber-font-size-sm)',
                fontWeight: 600,
                color: 'var(--psyber-text)'
              }}
            >
              <i className="fas fa-user"></i> Employee *
            </label>
            <select
              id="employee"
              value={selectedEmployee}
              onChange={(e) => setSelectedEmployee(e.target.value)}
              required
              disabled={uploading}
              className="psyber-input"
              style={{
                cursor: 'pointer'
              }}
            >
              <option value="">Select Employee</option>
              {employees.map(emp => (
                <option key={emp.uid} value={emp.uid}>
                  {emp.fullName} ({emp.employeeId})
                </option>
              ))}
            </select>
          </div>

          {/* Month Dropdown */}
          <div>
            <label
              htmlFor="month"
              style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontSize: 'var(--psyber-font-size-sm)',
                fontWeight: 600,
                color: 'var(--psyber-text)'
              }}
            >
              <i className="fas fa-calendar-alt"></i> Month *
            </label>
            <select
              id="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              required
              disabled={uploading}
              className="psyber-input"
              style={{
                cursor: 'pointer'
              }}
            >
              <option value="">Select Month</option>
              {getAllMonths().map(m => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>

          {/* Year Dropdown */}
          <div>
            <label
              htmlFor="year"
              style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontSize: 'var(--psyber-font-size-sm)',
                fontWeight: 600,
                color: 'var(--psyber-text)'
              }}
            >
              <i className="fas fa-calendar"></i> Year *
            </label>
            <select
              id="year"
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              required
              disabled={uploading}
              className="psyber-input"
              style={{
                cursor: 'pointer'
              }}
            >
              {getYearOptions().map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>

        {/* File Upload */}
        <div style={{ marginBottom: '1rem' }}>
          <label
            htmlFor="file"
            style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontSize: 'var(--psyber-font-size-sm)',
              fontWeight: 600,
              color: 'var(--psyber-text)'
            }}
          >
            <i className="fas fa-file-pdf"></i> PDF File * (Max 10MB)
          </label>
          <input
            type="file"
            id="file"
            accept=".pdf,application/pdf"
            onChange={handleFileChange}
            required
            disabled={uploading}
            className="psyber-input"
            style={{
              padding: '0.5rem',
              cursor: 'pointer'
            }}
          />

          {/* File Preview */}
          {file && (
            <div style={{
              marginTop: '0.75rem',
              padding: '0.75rem',
              background: 'var(--psyber-success-bg)',
              border: '1px solid var(--psyber-success)',
              borderRadius: 'var(--psyber-radius-md)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
            }}>
              <i className="fas fa-file-pdf" style={{ fontSize: '1.5rem', color: 'var(--psyber-success)' }}></i>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, color: 'var(--psyber-text)', fontSize: 'var(--psyber-font-size-sm)' }}>
                  {file.name}
                </div>
                <div style={{ fontSize: 'var(--psyber-font-size-xs)', color: 'var(--psyber-text-secondary)' }}>
                  {formatFileSize(file.size)}
                </div>
              </div>
              <i className="fas fa-check-circle" style={{ fontSize: '1.25rem', color: 'var(--psyber-success)' }}></i>
            </div>
          )}
        </div>

        {/* Error Display */}
        {error && (
          <div style={{
            marginBottom: '1rem',
            padding: '1rem',
            background: 'var(--psyber-error-bg)',
            border: '2px solid var(--psyber-error)',
            borderRadius: 'var(--psyber-radius-md)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem'
          }}>
            <i className="fas fa-exclamation-circle" style={{ fontSize: '1.5rem', color: 'var(--psyber-error)' }}></i>
            <span style={{ color: 'var(--psyber-error)', fontWeight: 600 }}>{error}</span>
          </div>
        )}

        {/* Success Display */}
        {success && (
          <div style={{
            marginBottom: '1rem',
            padding: '1rem',
            background: 'var(--psyber-success-bg)',
            border: '2px solid var(--psyber-success)',
            borderRadius: 'var(--psyber-radius-md)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem'
          }}>
            <i className="fas fa-check-circle" style={{ fontSize: '1.5rem', color: 'var(--psyber-success)' }}></i>
            <span style={{ color: 'var(--psyber-success)', fontWeight: 600 }}>{success}</span>
          </div>
        )}

        {/* Submit Button */}
        <div style={{
          display: 'flex',
          gap: '1rem',
          justifyContent: 'flex-end'
        }}>
          <Button
            type="button"
            variant="ghost"
            onClick={resetForm}
            disabled={uploading}
          >
            Reset
          </Button>
          <Button
            type="submit"
            variant="primary"
            icon="fas fa-upload"
            loading={uploading}
            disabled={!selectedEmployee || !month || !year || !file || uploading}
          >
            {uploading ? 'Uploading...' : 'Upload Salary Slip'}
          </Button>
        </div>
      </form>
    </div>
  )
}

export default UploadForm
